import React, { useContext, useState } from "react";
import { Formik, Form, Field } from "formik";
import { FormGroup, FormControl } from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { BsFillCalendarEventFill } from "react-icons/bs";
import { MdClose } from "react-icons/md";

import SelectPcp from "../../forms/SelectPcp";
import SendCprNotificationButton from "../SendCprNotificationButton";
import OrderDocument from "../../../shared/OrderDocument";
import OrderDocumentVerifiedCheckbox from "../../../shared/OrderDocumentVerifiedCheckbox";
import FieldError from "../../../shared/FieldError";
import { Settings } from "../../../helpers/Contexts";
import Lead from "../../../shared/Lead";
import * as CONST from "../../../helpers/Constants";
import * as API from "../../../api";

const CprOrderRequest = ({ task }) => {
  const settings = useContext(Settings);
  let servicingProvider = task.servicing_provider || task.cpr.servicing_provider;

  // SHOW CPR ORDER FORM if order document has not been uploaded or verified.
  const [isEditing, setIsEditing] = useState((
    (servicingProvider && task.cpr.order_referral_date && task.cpr.order_document.url) ||
    task.cpr.order_not_available_reason ||
    task.status == 'closed' ||
    task.cpr.order_document_verified === true
      ? false
      : true
  ));
  const [patientNotified, setPatientNotified] = useState(false);
  const [replaceOrder, setReplaceOrder] = useState(false);
  const [orderNotRequired, setOrderNotRequired] = React.useState(false);
  let showOrderDocumentForm = (!orderNotRequired && isEditing) || (task.task_type == 'cpr_status_check');
  let showOrderDocumentVerifiedForm = (!task.cpr.order_document_verified && task.cpr.order_document.url);

  const isColonCancerScreening = [
    ...CONST.COLON_CANCER_SCREENING,
    "Colon Cancer Screening",
  ].includes(task.cpr.procedure_type);

  const isPcpVisit = settings.visit_types.find((i) =>
    i.name == task.cpr.procedure_type ? true : false
  );

  const OrderDocumentFormFields = ({values, setFieldValue}) => {
    return(
      <>
        <label>{isPcpVisit ? "Visit Type" : "Procedure Type"}</label>
        {isColonCancerScreening ? (
          <>
            <br />
            <select
              className="time-selector"
              placeholder="Select procedure type"
              name="procedure_type"
              onChange={(e) => {
                setFieldValue(
                  "procedure_type_id",
                  settings.colon_cancer_procedure_types.filter(
                    (i) => i.name == e.target.value
                  )[0].id
                );
                if (e.target.value == "Cologuard") {
                  setFieldValue("servicing_provider", "Exact Sciences");
                } else {
                  values.servicing_provider == "Exact Sciences" &&
                    values.procedure_type !== "Cologuard" &&
                    setFieldValue("servicing_provider", "");
                }
              }}
            >
              <option value={""}>Select procedure for colon cancer screening</option>
              {CONST.COLON_CANCER_SCREENING.map((t, idx) => (
                <option key={idx} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {!values.procedure_type && <FieldError error={"Procedure type is required"} />}
          </>
        ) : (
          task.cpr.procedure_type
        )}

        <label>
          {"Servicing Provider "}
          <small id="from"> (The provider patient is being referred to.) </small>
        </label>

        {isPcpVisit ? (
          <SelectPcp
            name="servicing_pcp_id"
            required={true}
            value={values.pcp}
            setFieldValue={setFieldValue}
          />
        ) : (
          <>
            <Field name="servicing_provider" placeholder={""} />
          </>
        )}
        <br />
        {(!values.servicing_provider && !values.servicing_pcp_id) ? (
          <FieldError error={"Servicing provider is required"} />
        ) : null}
        <label>Order/Referral Date</label>
        <div className={"flex datefield-wrapper full-width"}>
          <Field name="order_referral_date" placeholder="Order/referral date">
            {({ field, meta }) => {
              return (
                <>
                  <BsFillCalendarEventFill className="calendar-icon" />
                  <ReactDatePicker
                    className="datepicker"
                    autoComplete="off"
                    required={true}
                    {...field}
                    maxDate={settings.me.environment == "production" ? new Date() : null}
                    dateFromat={"YYYY-MM-dd"}
                    onChange={(date) => {
                      setFieldValue("order_referral_date", date.toISOString().slice(0, 10));
                      // setFieldValue("date", date.toISOString().slice(0, 10));
                    }}
                  />
                </>
              );
            }}
          </Field>
        </div>

        {!values.order_referral_date ? (
          <FieldError error={"Order/Referral date is required"} />
        ) : null}

        <label>Order/Referral Document</label>
        {!replaceOrder && task.cpr.order_document.url ? (
          <OrderDocument formHandler={task.formHandler} url={task.cpr.order_document.url} />
        ) : (
          <>
            <FormGroup controlId="formFile" className="mb-3">
              <FormControl
                type="file"
                onChange={(e) => setFieldValue("order_document", e.target.files[0])}
              />
            </FormGroup>
          </>
        )}
        { task.cpr.order_document.url &&
          (<>
            <a
              className="btn btn-default mt4"
              onClick={(e) => setReplaceOrder(!replaceOrder)}
            >
              {!replaceOrder
                ? "Replace Order/Referral Document ?"
                : "Cancel (keep current order/referral document)"}
            </a>
          </>)
        }
        {!values.order_document && <FieldError error={"Order/Referral Document is required"} /> }
      </>
    );
  };

  let initValues = {
    servicing_provider: task.cpr.servicing_provider ? task.cpr.servicing_provider : "",
    order_referral_date: task.cpr.order_referral_date ? task.cpr.order_referral_date : "",
    order_document: "",
    procedure_type: task.cpr.procedure_type,
  };
  isColonCancerScreening
    ? (initValues = {
        ...initValues,
        procedure_type_id: task.cpr.procedure_type.includes("Colon Cancer Screening")
          ? ""
          : settings.colon_cancer_procedure_types.filter(
              (i) => i.name == task.cpr.procedure_type
            )[0].id,
      })
    : (initValues = initValues);

  return (
    <>
      <Formik
        initialValues={{ ...initValues }}
        onSubmit={(values) => {
          const formData = new FormData();

          values.servicing_pcp_id && formData.append('task[servicing_pcp_id]', values.servicing_pcp_id);
          values.order_referral_date &&
            formData.append("task[order_referral_date]", values.order_referral_date);
          values.servicing_provider &&
            formData.append("task[servicing_provider]", values.servicing_provider);
          formData.append("task[order_document]", values.order_document);

          if (values.procedure_type_id) {
            formData.append("task[procedure_type_id]", values.procedure_type_id);
          }

          if (values.order_not_available_reason) {
            formData.append("task[order_not_available_reason]", values.order_not_available_reason);
          }

          if (values.order_not_available_reason_verified) {
            formData.append("task[order_not_available_reason_verified]", values.order_not_available_reason_verified);
          }
          formData.append("task[latest_quality_measurement]", true);
          formData.append("task[event]", "cpr_not_completed");
          formData.append("task[assigner_id]", settings.me.id);
          formData.append("task[id]", task.id);
          return task.formHandler(formData);
        }}
      >
        {({ values, setFieldValue, setTouched, errors, valid }) => {
          const renderForm = (
            <>
              <h4 style={{ marginBottom: "0px" }}>Order Details: </h4>
              <Form>
                <div>
                  <label> Order Not Available?
                    <input
                      style={{
                        margin: "0px 18px",
                        display: "inline-block",
                        verticalAlign: "middle"
                      }}
                      value={true}
                      onClick={(el) => {
                        if (el.target.checked == true) {
                          setOrderNotRequired(true);
                        } else {
                          setOrderNotRequired(false);
                        }
                      }}
                      className={'form-control form-field width-auto'} type={'checkbox'}
                    />
                  </label>
                  {
                    orderNotRequired && (<>
                      <label> Please provide a reason why an order is not available: </label>
                      <textarea
                        type="textarea"
                        required={true}
                        onChange={(el) => setFieldValue("order_not_available_reason", el.target.value)}
                      ></textarea>
                      <br />
                      <i>
                        wellPORTAL policy for care plan requirement (CPR) orders/referrals. <br />
                        If the patient has been seen in the last 12 months, the PCP should provide CPR orders/referrals.<br />
                        If the patient has not been seen in the last 12 months, the PCP may require the patient to come in for a visit to obtain updated orders/referrals.
                      </i>
                    </>)
                  }
                </div>
                { showOrderDocumentForm && <OrderDocumentFormFields values={values} setFieldValue={setFieldValue}/>}
                <button className="check accent-bg" type="submit">
                  Submit
                </button>
              </Form>
            </>
          );

          const renderOrderValues = (
            <>
              {
                !task.cpr.order_not_available_reason && (<>
                  <h4 style={{ marginBottom: "0px" }}>Order Details:</h4>
                  <label>Procedure Type</label>
                  {task.cpr.procedure_type}
                  <label>
                    Servicing Provider
                    <small id="from"> (The provider patient is being referred to.) </small>
                  </label>
                  {values.servicing_provider}
                  <label>Order/Referral Date</label>
                  {values.order_referral_date}
                  {task.cpr.order_document.url && (
                    <>
                      <label>Order/Referral Document</label>
                      <OrderDocument formHandler={task.formHandler} url={task.cpr.order_document.url} />
                    </>
                  )}
                </>)
              }

              {task.cpr.order_not_available_reason && (<>
                <label>Order Not Available Reason: </label>
                <br />
                { task.cpr.order_not_available_reason }
                <label>
                  Reason (Order Not Available) Verified?
                  <FormControl
                    style={{
                      margin: "0px 18px",
                      display: "inline-block",
                      verticalAlign: "middle",
                      width: 'auto'
                    }}
                    type="checkbox"
                    required={true}
                    value={task.cpr.order_not_available_reason_verified}
                    checked={task.cpr.order_not_available_reason_verified}
                    onChange={(e) => {
                      const formData = new FormData();
                      formData.append('task[id]', task.id);
                      formData.append("task[status]", "closed");
                      formData.append('task[order_not_available_reason_verified]', e.target.checked);
                      return task.formHandler(formData);
                    }}
                  />
                </label>
                </>)
              }

              {task.cpr.order_document.url && task.cpr.order_document_verified && (<>
                <br />
                <div className='m-t-lg'>
                  <i className={'fa fa-check-circle green-text'} /> Order Document Verified
                </div>
                <br />
                </>)
              }

              { showOrderDocumentVerifiedForm && (
                <OrderDocumentVerifiedCheckbox
                  task={task}
                  settings={settings}
                />
              )}
            </>
          );

          return (
            <>
              <p>
                {task.task_type !== "quality_measure_status_check" ? (
                  <p>
                    <b>{CONST.CPR_ORDER_REQUEST}</b>
                  </p>
                ) : <b>{CONST.CPR_ORDER_SUBMITTED}</b>}
              </p>
              <div className="cpr-template">
                <a
                  className={isEditing ? "cancel small float-right" : "float-right"}
                  onClick={() => {
                    setIsEditing(!isEditing);
                    isColonCancerScreening &&
                      setFieldValue("procedure_type", "") &&
                      setFieldValue("procedure_type_id", "");
                    replaceOrder && setReplaceOrder(false);
                  }}
                >
                  {!isEditing ? (
                    <i className="fa fa-edit blue-text" />
                  ) : (
                    <MdClose className="cancel-icon" />
                  )}
                </a>
                {isEditing ? renderForm : renderOrderValues}

                {(
                  task.status == "closed" |
                   task.cpr.order_document_verified ||
                   task.cpr.order_not_available_reason_verified
                 ) &&
                (
                  <SendCprNotificationButton
                    task={task}
                    patientNotified={patientNotified}
                    setPatientNotified={setPatientNotified}
                  />
                )}
              </div>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default CprOrderRequest;
